(() => {
  const initialize = (root) => {
    if (!root || root.dataset.ltkInitialized === 'true') return;
    root.dataset.ltkInitialized = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const tabs = [...root.querySelectorAll('[data-ltk-tab]')];
    const panels = [...root.querySelectorAll('[data-ltk-panel]')];
    const rail = root.querySelector('[data-ltk-books-rail]');
    const previousButton = root.querySelector('[data-ltk-books-previous]');
    const nextButton = root.querySelector('[data-ltk-books-next]');
    const progress = root.querySelector('[data-ltk-books-progress]');
    const comingSoonTriggers = [...root.querySelectorAll('[data-ltk-coming-soon-trigger]')];
    const comingSoonDialog = root.querySelector('[data-ltk-coming-soon-dialog]');
    const comingSoonTitle = comingSoonDialog?.querySelector('[data-ltk-coming-soon-title]');
    const comingSoonMessage = comingSoonDialog?.querySelector('[data-ltk-coming-soon-message]');
    const comingSoonClose = comingSoonDialog?.querySelector('[data-ltk-coming-soon-close]');
    const cleanup = [];
    let activeComingSoonTrigger = null;

    const updateBooks = () => {
      if (!rail) return;
      const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
      const ratio = maxScroll ? rail.scrollLeft / maxScroll : 0;
      if (previousButton) previousButton.disabled = rail.scrollLeft <= 2;
      if (nextButton) nextButton.disabled = rail.scrollLeft >= maxScroll - 2;
      if (progress) progress.style.transform = `translateX(${ratio * 233}%)`;
    };

    const activateTab = (name, focusTab = false, updateHash = true) => {
      const targetTab = tabs.find((tab) => tab.dataset.ltkTab === name) || tabs[0];
      if (!targetTab) return;

      tabs.forEach((tab) => {
        const isActive = tab === targetTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.ltkPanel !== targetTab.dataset.ltkTab;
      });

      if (focusTab) targetTab.focus();
      if (updateHash && window.history?.replaceState) {
        const nextUrl = `${window.location.pathname}${window.location.search}#${targetTab.dataset.ltkTab}`;
        window.history.replaceState(null, '', nextUrl);
      }
      window.requestAnimationFrame(updateBooks);
    };

    tabs.forEach((tab, index) => {
      const handleClick = () => activateTab(tab.dataset.ltkTab);
      const handleKeydown = (event) => {
        let targetIndex = index;
        if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = tabs.length - 1;
        if (targetIndex === index) return;
        event.preventDefault();
        activateTab(tabs[targetIndex].dataset.ltkTab, true);
      };
      tab.addEventListener('click', handleClick);
      tab.addEventListener('keydown', handleKeydown);
      cleanup.push(() => tab.removeEventListener('click', handleClick));
      cleanup.push(() => tab.removeEventListener('keydown', handleKeydown));
    });

    const hashTab = window.location.hash.replace('#', '');
    const initialTab = tabs.some((tab) => tab.dataset.ltkTab === hashTab)
      ? hashTab
      : root.dataset.initialTab;
    activateTab(initialTab, false, false);

    if (comingSoonDialog && comingSoonTriggers.length) {
      const closeComingSoonDialog = () => {
        if (comingSoonDialog.open) comingSoonDialog.close();
      };
      const handleDialogClose = () => {
        activeComingSoonTrigger?.focus();
        activeComingSoonTrigger = null;
      };
      const handleDialogClick = (event) => {
        if (event.target === comingSoonDialog) closeComingSoonDialog();
      };

      comingSoonTriggers.forEach((trigger) => {
        const handleComingSoonClick = () => {
          activeComingSoonTrigger = trigger;
          if (comingSoonTitle) {
            comingSoonTitle.textContent =
              trigger.dataset.ltkComingSoonLabel || root.dataset.ltkComingSoonDefaultLabel || '';
          }
          if (comingSoonMessage) comingSoonMessage.textContent = trigger.dataset.ltkComingSoonMessage || '';
          if (!comingSoonDialog.open) comingSoonDialog.showModal();
          comingSoonClose?.focus();
        };
        trigger.addEventListener('click', handleComingSoonClick);
        cleanup.push(() => trigger.removeEventListener('click', handleComingSoonClick));
      });

      comingSoonClose?.addEventListener('click', closeComingSoonDialog);
      comingSoonDialog.addEventListener('click', handleDialogClick);
      comingSoonDialog.addEventListener('close', handleDialogClose);
      cleanup.push(() => comingSoonClose?.removeEventListener('click', closeComingSoonDialog));
      cleanup.push(() => comingSoonDialog.removeEventListener('click', handleDialogClick));
      cleanup.push(() => comingSoonDialog.removeEventListener('close', handleDialogClose));
      cleanup.push(() => {
        if (comingSoonDialog.open) comingSoonDialog.close();
      });
    }

    if (rail) {
      const scrollBooks = (direction) => {
        const distance = Math.max(rail.clientWidth * 0.82, 240);
        rail.scrollBy({ left: direction * distance, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };
      const handlePrevious = () => scrollBooks(-1);
      const handleNext = () => scrollBooks(1);
      const handleScroll = () => window.requestAnimationFrame(updateBooks);
      previousButton?.addEventListener('click', handlePrevious);
      nextButton?.addEventListener('click', handleNext);
      rail.addEventListener('scroll', handleScroll, { passive: true });
      cleanup.push(() => previousButton?.removeEventListener('click', handlePrevious));
      cleanup.push(() => nextButton?.removeEventListener('click', handleNext));
      cleanup.push(() => rail.removeEventListener('scroll', handleScroll));

      const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateBooks) : null;
      resizeObserver?.observe(rail);
      cleanup.push(() => resizeObserver?.disconnect());
      updateBooks();
    }

    const revealItems = [...root.querySelectorAll('[data-ltk-reveal]')];
    if (!reduceMotion.matches && 'IntersectionObserver' in window && revealItems.length) {
      root.classList.add('ltk-motion-ready');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
      revealItems.forEach((item, index) => {
        item.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
        observer.observe(item);
      });
      cleanup.push(() => observer.disconnect());
    } else {
      revealItems.forEach((item) => item.classList.add('is-revealed'));
    }

    root._ltkCleanup = () => {
      cleanup.forEach((callback) => callback());
      delete root.dataset.ltkInitialized;
    };
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-ltk-root]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ltk-root]').forEach((root) => root._ltkCleanup?.());
  });
})();
